"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const clsx_1 = __importDefault(require("clsx"));
const react_i18next_1 = require("react-i18next");
const google_maps_context_provider_1 = require("../google-maps-context-provider");
const use_when_changed_1 = __importDefault(require("../../utils/use-when-changed"));
const utils_1 = require("./utils");
const loader_1 = __importDefault(require("./loader"));
const input_field_1 = __importDefault(require("./input-field"));
const predictions_view_1 = __importDefault(require("./predictions-view"));
require("./styles.scss");
const LocationAutocomplete = ({ htmlId, location, setLocation, locating, error, onFocus, validateFuncRef, customLocationIcon, customLocations, ariaInvalid, className }) => {
    var _a;
    const { i18n } = (0, react_i18next_1.useTranslation)();
    const { isLoaded } = (0, react_1.useContext)(google_maps_context_provider_1.GoogleMapsContext);
    const [timedOut, setTimedOut] = (0, react_1.useState)(false);
    const [callFocus, setCallFocus] = (0, react_1.useState)(false);
    const [open, setOpen] = (0, react_1.useState)(false);
    const [activeIndex, setActiveIndex] = (0, react_1.useState)(0);
    const [value, setValue] = (0, react_1.useState)((_a = location === null || location === void 0 ? void 0 : location.name) !== null && _a !== void 0 ? _a : "");
    const [selectedPlaceId, setSelectedPlaceId] = (0, react_1.useState)(null);
    const [predictions, setPredictions] = (0, react_1.useState)([]);
    const ignoreBlur = (0, react_1.useRef)(false);
    const inputRef = (0, react_1.useRef)(null);
    const listboxRef = (0, react_1.useRef)(null);
    const activeOptionRef = (0, react_1.useRef)(null);
    const autocompleteService = (0, react_1.useRef)(null);
    const autocompleteSessionToken = (0, react_1.useRef)(null);
    const placesService = (0, react_1.useRef)(null);
    if (isLoaded) {
        if (autocompleteService.current === null) {
            autocompleteService.current = new google.maps.places.AutocompleteService();
        }
        if (autocompleteSessionToken.current === null) {
            autocompleteSessionToken.current = new google.maps.places.AutocompleteSessionToken();
        }
        if (placesService.current === null) {
            placesService.current = new google.maps.places.PlacesService(document.createElement("div"));
        }
    }
    (0, use_when_changed_1.default)((name) => {
        setValue(name !== null && name !== void 0 ? name : "");
    }, location === null || location === void 0 ? void 0 : location.name);
    const updateOption = (index) => {
        var _a;
        const option = predictions[index];
        if (!option)
            return;
        if ((0, utils_1.isCustomLocation)(option)) {
            window.location.href = i18n.language === "fr" ? option.url_fr : option.url_en;
            return;
        }
        if (!placesService.current)
            return;
        placesService.current.getDetails({
            placeId: option.place_id,
            fields: ["name", "formatted_address", "geometry.location", "geometry.viewport"],
            sessionToken: (_a = autocompleteSessionToken.current) !== null && _a !== void 0 ? _a : undefined,
        }, (result) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
            if (!result) {
                return;
            }
            const newLocation = {
                name: (_b = (_a = option.description) !== null && _a !== void 0 ? _a : result.name) !== null && _b !== void 0 ? _b : "Unknown location",
                lat: (_e = (_d = (_c = result.geometry) === null || _c === void 0 ? void 0 : _c.location) === null || _d === void 0 ? void 0 : _d.lat()) !== null && _e !== void 0 ? _e : NaN,
                lng: (_h = (_g = (_f = result.geometry) === null || _f === void 0 ? void 0 : _f.location) === null || _g === void 0 ? void 0 : _g.lng()) !== null && _h !== void 0 ? _h : NaN,
                viewport: (_k = (_j = result.geometry) === null || _j === void 0 ? void 0 : _j.viewport) === null || _k === void 0 ? void 0 : _k.toJSON(),
            };
            if (!newLocation || isNaN(newLocation.lat) || isNaN(newLocation.lng)) {
                return;
            }
            setValue(newLocation.name);
            setLocation(newLocation);
        });
        autocompleteSessionToken.current = new google.maps.places.AutocompleteSessionToken();
        setSelectedPlaceId(option.place_id);
        setActiveIndex(index);
        setOpen(false);
    };
    const onOptionClick = (index) => {
        setActiveIndex(index);
        updateOption(index);
    };
    const onOptionMouseDown = (index) => {
        ignoreBlur.current = true;
        setCallFocus(true);
        setActiveIndex(index);
    };
    // This is a bit too clever. But React normally doesn't expose a "validation" function from input child components that parent components can call. This is because normally you want to use
    // controlled inputs, but ... those aren't great for performance. So an alternative is as follows: using a ref to pass back to the parent a validate function from the child.
    if (validateFuncRef) {
        validateFuncRef.current = () => {
            if (locating === true || inputRef.current === null || inputRef.current.value === "") {
                return {
                    valid: false,
                    error: "ENTER_LOCATION_TO_CONTINUE",
                };
            }
            if ((location === null || location === void 0 ? void 0 : location.name) === inputRef.current.value && !isNaN(location.lat) && !isNaN(location.lng)) {
                return {
                    valid: true,
                };
            }
            return {
                valid: false,
                error: "GEOCODE_REQUIRED",
                input: inputRef.current.value,
            };
        };
    }
    (0, react_1.useEffect)(() => {
        if (inputRef.current && callFocus === true) {
            inputRef.current.focus();
            setCallFocus(false);
        }
    }, [callFocus, setCallFocus, open]);
    (0, react_1.useEffect)(() => {
        if (open && (0, utils_1.isScrollable)(listboxRef)) {
            (0, utils_1.maintainScrollVisibility)(activeOptionRef, listboxRef);
        }
    }, [open, activeIndex]);
    return ((0, jsx_runtime_1.jsxs)("div", Object.assign({ className: `${(0, clsx_1.default)({ autocomplete: true, open })} ${className ? className : ""}` }, { children: [locating ? (0, jsx_runtime_1.jsx)(loader_1.default, {}) :
                (0, jsx_runtime_1.jsx)(input_field_1.default, { htmlId: htmlId, open: open, error: error, inputRef: inputRef, value: value, setValue: setValue, onFocus: onFocus, setOpen: setOpen, setCallFocus: setCallFocus, autocompleteSessionToken: autocompleteSessionToken, autocompleteService: autocompleteService, timedOut: timedOut, setTimedOut: setTimedOut, predictions: predictions, setPredictions: setPredictions, activeIndex: activeIndex, setActiveIndex: setActiveIndex, customLocations: customLocations, ignoreBlur: ignoreBlur, updateOption: updateOption, location: location, setLocation: setLocation, "aria-invalid": ariaInvalid }), predictions &&
                (0, jsx_runtime_1.jsx)(predictions_view_1.default, { htmlId: htmlId, listboxRef: listboxRef, ignoreBlur: ignoreBlur, timedOut: timedOut, predictions: predictions, activeIndex: activeIndex, customLocationIcon: customLocationIcon, selectedPlaceId: selectedPlaceId, activeOptionRef: activeOptionRef, onOptionClick: onOptionClick, onOptionMouseDown: onOptionMouseDown })] })));
};
exports.default = LocationAutocomplete;
